// Bar chart body — sibling of EnumPieBody. Same data, same colors, same
// ordering rules; just rendered as vertical bars so magnitudes are easier
// to compare when the enum has many values.

import React, { useEffect, useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { api } from '../../api/client';
import type { ChartBodyProps } from '../types';
import { useTimeRangeState, windowForAnchor, withinWindow } from '../windowRange';
import ChartTimeRangePicker from '../ChartTimeRangePicker';
import {
  BAR_CATEGORY_GAP, BAR_TICK, BAR_TOOLTIP_CURSOR, BarTooltipCard, barChartMargin,
} from './barChartStyles';
import { assignSliceColors } from './chartColors';

interface ValueRow {
  date: string;
  freeform_value: string | null;
}

const EnumBarBody: React.FC<ChartBodyProps> = ({ featureId, config, fullscreen }) => {
  const [rows, setRows] = useState<ValueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get<ValueRow[]>(`/api/features/values/${featureId}`)
      .then((resp) => { if (!cancelled) { setRows(resp.data); setError(null); } })
      .catch((err) => {
        console.error('Failed to fetch feature values:', err);
        if (!cancelled) setError('Failed to load distribution data');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [featureId]);

  const dataDates = useMemo(() => rows.map((r) => new Date(r.date).getTime()), [rows]);
  const range = useTimeRangeState(dataDates);
  const win = useMemo(
    () => windowForAnchor(range.granularity, range.anchor),
    [range.granularity, range.anchor],
  );

  const bars = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of rows) {
      if (!withinWindow(new Date(r.date).getTime(), win)) continue;
      const v = r.freeform_value;
      if (v == null || v === '') continue;
      counts.set(v, (counts.get(v) ?? 0) + 1);
    }
    const declared = (config?.params?.values ?? []).map(String);
    const orderedKeys: string[] = [];
    const seen = new Set<string>();
    for (const k of declared) {
      if (counts.has(k) && !seen.has(k)) { orderedKeys.push(k); seen.add(k); }
    }
    const extras = [...counts.keys()].filter((k) => !seen.has(k))
      .sort((a, b) => (counts.get(b)! - counts.get(a)!));
    for (const k of extras) orderedKeys.push(k);

    const colors = assignSliceColors(orderedKeys);
    return orderedKeys.map((key, i) => ({
      name: key,
      value: counts.get(key) ?? 0,
      color: colors[i],
    }));
  }, [rows, config, win]);

  const total = bars.reduce((acc, b) => acc + b.value, 0);

  if (loading) {
    return (
      <div className={`flex justify-center items-center ${fullscreen ? 'h-full' : 'h-64'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600" />
      </div>
    );
  }
  if (error) {
    return <div className="text-center py-8 text-rose-500"><p>{error}</p></div>;
  }
  if (rows.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No values recorded yet for this feature.</p>
      </div>
    );
  }

  return (
    <div className={fullscreen ? 'h-full flex flex-col' : 'flex flex-col'} style={{ touchAction: 'none' }}>
      <ChartTimeRangePicker state={range} />
      <div className={fullscreen ? 'flex-1 min-h-0' : 'h-80'}>
        {total === 0 ? (
          <div className="flex items-center justify-center h-full text-center text-gray-500">
            <p>No values in this period.</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={bars}
              margin={barChartMargin(fullscreen, 'categorical-angled')}
              barCategoryGap={BAR_CATEGORY_GAP}
            >
              <XAxis
                dataKey="name"
                tick={BAR_TICK}
                angle={-30}
                textAnchor="end"
                interval={0}
                height={fullscreen ? 70 : 50}
                tickLine={false}
              />
              <YAxis allowDecimals={false} tick={BAR_TICK} tickLine={false} />
              <Tooltip
                cursor={BAR_TOOLTIP_CURSOR}
                content={({ active, payload }: { active?: boolean; payload?: Array<{ value: number; payload: { name: string } }> }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const value = payload[0].value;
                  const name = payload[0].payload?.name ?? '';
                  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                  return (
                    <BarTooltipCard>
                      {name && <div className="text-gray-600">{name}</div>}
                      <div className="text-gray-900">{value} checkin{value === 1 ? '' : 's'} ({pct}%)</div>
                    </BarTooltipCard>
                  );
                }}
              />
              <Bar dataKey="value" isAnimationActive={true}>
                {bars.map((b) => <Cell key={b.name} fill={b.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default EnumBarBody;
